import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// 🔹 Queued Call Interface
export interface QueuedCall {
  memberCIDName: string;
  memberCIDNumber: string;
  joinedEpoch: string;
  queueName: string;
  sessionUUID: string;
  queue: string;
  agentName: string;
  [key: string]: any;
}

// 🔹 Slice State Interface
interface CallCenterCallsState {
  items: QueuedCall[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

// 🔹 Initial State
const initialState: CallCenterCallsState = {
  items: [],
  status: 'idle',
  error: null,
};

// 🔹 Add/Update queue calls (normalize to array)
export const updateCallCenterCalls = createAsyncThunk<QueuedCall[], QueuedCall[] | QueuedCall>(
  'callCenterCalls/update',
  async (calls) => (Array.isArray(calls) ? calls : [calls])
);

// 🔹 Remove queued call by session UUID
export const removeCallCenterCallByAgentId = createAsyncThunk<string, string>(
  'callCenterCalls/remove',
  async (sessionUUID) => sessionUUID
);

// 🔹 Clear all queued calls
export const removeAllCallCenterHoldCalls = createAsyncThunk<boolean>(
  'callCenterCalls/clearAll',
  async () => true
);

// 🔹 Slice
const callCenterCallsSlice = createSlice({
  name: 'callCenterCalls',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // --- Update ---
      .addCase(updateCallCenterCalls.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateCallCenterCalls.fulfilled, (state, action: PayloadAction<QueuedCall[]>) => {
        state.status = 'succeeded';

        action.payload.forEach((call) => {
          const existingIndex = state.items.findIndex(
            (item) => item.sessionUUID === call.sessionUUID
          );

          if (existingIndex !== -1) {
            state.items[existingIndex] = call;
          } else {
            state.items.push(call);
          }
        });
      })
      .addCase(updateCallCenterCalls.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? 'Unknown error';
      })

      // --- Remove by ID ---
      .addCase(removeCallCenterCallByAgentId.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(removeCallCenterCallByAgentId.fulfilled, (state, action: PayloadAction<string>) => {
        const sessionUUID = action.payload;
        state.items = state.items.filter((item) => item.sessionUUID !== sessionUUID);
      })
      .addCase(removeCallCenterCallByAgentId.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? 'Unknown error';
      })

      // --- Clear all ---
      .addCase(removeAllCallCenterHoldCalls.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(removeAllCallCenterHoldCalls.fulfilled, (state) => {
        state.status = 'succeeded';
        state.items = [];
      })
      .addCase(removeAllCallCenterHoldCalls.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? 'Unknown error';
      });
  },
});

export default callCenterCallsSlice.reducer;
